import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportDataTable, {
  ReportDataTableColumn,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import ReportFullViewTableTitle, {
  useReportFullViewSection,
} from '@components/pages/Reports/shared/ReportFullViewTableTitle.component'
import ReportExpandableSection from '@components/pages/Reports/shared/ReportExpandableSection.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  HygieneOverdueRow,
  ProviderRecallResponseRow,
  RECALL_REACTIVATION_CONTROL_FILTERS,
  RECALL_REACTIVATION_CONTROL_KPI_DEFINITIONS,
} from '@constants/Reports/recallReactivationControl.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useRecallReactivationControl } from '@hooks/reports/useRecallReactivationControl'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import { formatCount, formatPercent } from '@utils/dashboardFinance.util'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { formatKpiPercent } from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_LINE_CHART_MARGIN = { top: 8, right: 12, left: 4, bottom: 2 }
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 46
const REPORT_YAXIS_WIDTH_PERCENT = 40

const HYGIENE_OVERDUE_PHONE_COLUMN: ReportDataTableColumn<HygieneOverdueRow> = {
  key: 'phone',
  label: 'Phone',
}

function formatLastHygiene(value: string | null): string {
  if (!value) return '—'
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function computePercentAxis(values: number[]): {
  domain: [number, number]
  ticks: number[]
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax =
    max <= 0 ? 100 : Math.min(100, Math.ceil((max * 1.15) / 5) * 5)
  const step = axisMax <= 25 ? 5 : axisMax <= 50 ? 10 : 20
  const ticks: number[] = []
  for (let t = 0; t <= axisMax; t += step) ticks.push(t)
  return { domain: [0, axisMax], ticks }
}

function computeCountAxis(values: number[]): {
  domain: [number, number]
  ticks: number[]
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax = max <= 0 ? 10 : Math.ceil((max * 1.15) / 10) * 10
  const step = axisMax <= 20 ? 5 : axisMax <= 50 ? 10 : Math.ceil(axisMax / 4)
  const ticks: number[] = []
  for (let t = 0; t <= axisMax; t += step) ticks.push(t)
  return { domain: [0, axisMax], ticks }
}

function RecallReactivationControlReport(): React.ReactElement {
  const [reactivationView, setReactivationView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const providerRecallFullView = useReportFullViewSection('rrc-provider')
  const hygieneOverdueFullView = useReportFullViewSection('rrc-hygiene')
  const { loading, error, data } = useRecallReactivationControl(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load recall and reactivation data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return RECALL_REACTIVATION_CONTROL_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        unitInline: def.unitInline,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      'recall-compliance': data.recallCompliance.value,
      'reactivation-rate': data.reactivationRate.value,
      'patients-due-recall': data.patientsDueForRecall.value,
      'hygiene-overdue': data.hygieneOverdue.value,
    }

    return RECALL_REACTIVATION_CONTROL_KPI_DEFINITIONS.map((def) => {
      const raw = valueById[def.id]
      const value =
        def.format === 'percent' ? formatKpiPercent(raw) : formatCount(raw ?? 0)
      return {
        id: def.id,
        title: def.title,
        footer: def.footer,
        unitInline: def.format === 'percent' ? undefined : def.unitInline,
        value,
      }
    })
  }, [data, loading])

  const complianceTrendData = useMemo(() => {
    if (!data) return []
    return data.recallComplianceTrend.buckets.map((row) => ({
      month: row.label,
      rate: row.value,
    }))
  }, [data])

  const complianceYAxis = useMemo(() => {
    const values = complianceTrendData.map((row) => row.rate)
    return computePercentAxis(values)
  }, [complianceTrendData])

  const providerRecallRows = useMemo((): ProviderRecallResponseRow[] => {
    if (!data) return []
    return data.providerRecallResponse.rows.map((row) => ({
      id: row.providerId,
      provider: row.provider,
      eligible: row.eligible,
      completed: row.completed,
      rate: formatPercent(row.ratePct, 0),
    }))
  }, [data])

  const providerRecallColumns: ReportDataTableColumn<ProviderRecallResponseRow>[] =
    useMemo(() => {
      const cardColumns: ReportDataTableColumn<ProviderRecallResponseRow>[] = [
        { key: 'provider', label: 'Provider' },
        {
          key: 'eligible',
          label: 'Eligible',
          headerClassName: 'reports-patient-value-revenue__table-head--num',
          cellClassName: 'reports-patient-value-revenue__table-cell--num',
        },
        {
          key: 'completed',
          label: 'Completed',
          headerClassName: 'reports-patient-value-revenue__table-head--num',
          cellClassName: 'reports-patient-value-revenue__table-cell--num',
        },
      ]
      if (!providerRecallFullView.isFullView) return cardColumns
      return [
        ...cardColumns,
        {
          key: 'rate',
          label: 'Rate',
          headerClassName: 'reports-patient-value-revenue__table-head--num',
          cellClassName: 'reports-patient-value-revenue__table-cell--num',
        },
      ]
    }, [providerRecallFullView.isFullView])

  const hygieneOverdueRows = useMemo((): HygieneOverdueRow[] => {
    if (!data) return []
    return data.hygieneOverduePatients.rows.map((row) => ({
      id: row.patientId,
      patientName: row.patientName,
      lastHygiene: formatLastHygiene(row.lastHygiene),
      daysSince: row.daysSinceLastVisit,
      phone: row.phone || '—',
    }))
  }, [data])

  const hygieneOverdueColumns: ReportDataTableColumn<HygieneOverdueRow>[] =
    useMemo(
      () => [
        { key: 'patientName', label: 'Patient Name' },
        { key: 'lastHygiene', label: 'Last Hygiene' },
        {
          key: 'daysSince',
          label: 'Days Since',
          headerClassName: 'reports-patient-value-revenue__table-head--num',
          cellClassName: 'reports-patient-value-revenue__table-cell--num',
        },
        ...(hygieneOverdueFullView.isFullView
          ? [HYGIENE_OVERDUE_PHONE_COLUMN]
          : []),
      ],
      [hygieneOverdueFullView.isFullView],
    )

  const reactivationChartData = useMemo(() => {
    if (!data) return []
    return data.reactivationTrend.buckets.map((row) => ({
      month: row.label,
      count: row.count,
      percentage: row.percentage,
      barValue: reactivationView === 'Count' ? row.count : row.percentage,
      topLabel:
        reactivationView === 'Count' ? String(row.count) : `${row.percentage}%`,
    }))
  }, [data, reactivationView])

  const reactivationYAxis = useMemo(() => {
    if (reactivationView === 'Count') {
      const values = reactivationChartData.map((row) => row.count)
      return {
        ...computeCountAxis(values),
        tickFormatter: (v: number) => String(v),
      }
    }
    const values = reactivationChartData.map((row) => row.percentage)
    return {
      ...computePercentAxis(values),
      tickFormatter: (v: number) => `${v}%`,
    }
  }, [reactivationChartData, reactivationView])

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.recallCompliance.value,
      data?.reactivationRate.value,
      data?.patientsDueForRecall.value,
      data?.hygieneOverdue.value,
    ),
  )
  const complianceTrendEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.recallComplianceTrend?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const providerRecallEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.providerRecallResponse?.rows ?? []).some((row) =>
      hasNonZeroDashboardValues(row.eligible, row.completed, row.ratePct),
    ),
  )
  const reactivationEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.reactivationTrend?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.count, row.percentage),
    ),
  )
  const hygieneOverdueEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hygieneOverdueRows.length > 0,
  )

  return (
    <div className="reports-patient-base-health reports-recall-reactivation-control">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Recall &amp; Reactivation Control">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={RECALL_REACTIVATION_CONTROL_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      <div className="reports-recall-reactivation-control__stack">
        <ReportExpandableSection sectionId="rrc-kpi">
          <div className="reports-recall-reactivation-control__kpi">
            {kpiEmpty ? (
              <DashboardSectionEmptyState />
            ) : (
              <ReportKpiGrid items={kpiItems} />
            )}
          </div>
        </ReportExpandableSection>
        <div className="reports-recall-reactivation-control__grid">
          <ReportExpandableSection sectionId="rrc-trend">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Recall Compliance Rate (Trend)
                  </ReportSectionHeading>
                </div>
                <p className="reports-recall-reactivation-control__chart-note">
                  Completed recall visits/eligible recall patients
                </p>
                {complianceTrendEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-recall-reactivation-control__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={complianceTrendData}
                        margin={REPORT_LINE_CHART_MARGIN}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                        />
                        <YAxis
                          domain={complianceYAxis.domain}
                          ticks={complianceYAxis.ticks}
                          tickFormatter={(v) => `${v}%`}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={REPORT_YAXIS_WIDTH_PERCENT}
                        />
                        <Tooltip
                          formatter={(value: number) => `${value}%`}
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Line
                          type="linear"
                          dataKey="rate"
                          stroke="var(--blue-500)"
                          strokeWidth={2}
                          dot={false}
                          activeDot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="rrc-provider">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column reports-patient-value-revenue__column--fill">
                <div className="reports-patient-base-health__chart-head">
                  <ReportFullViewTableTitle
                    sectionId="rrc-provider"
                    title="Provider-specific Recall Response"
                    disableEnter={providerRecallEmpty}
                  />
                </div>
                <p className="reports-recall-reactivation-control__chart-note">
                  Eligible vs completed recall (90 days)
                </p>
                {providerRecallEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-recall-reactivation-control__table-block">
                    <ReportDataTable
                      key={
                        providerRecallFullView.isFullView
                          ? 'rrc-provider-fv'
                          : 'rrc-provider-card'
                      }
                      columns={providerRecallColumns}
                      rows={providerRecallRows}
                      rowKey={(row) => row.id}
                      initialPageSize={5}
                      className={[
                        'reports-patient-value-revenue__provider-table',
                        'reports-recall-reactivation-control__provider-table',
                        providerRecallFullView.isFullView
                          ? 'reports-patient-value-revenue__provider-table--full-view'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      fullViewSectionId="rrc-provider"
                      fullViewSearchWithFilter
                    />
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>

        <div className="reports-recall-reactivation-control__grid">
          <ReportExpandableSection sectionId="rrc-reactivation">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Reactivation Rate (Trend)
                  </ReportSectionHeading>
                  {reactivationEmpty ? null : (
                    <ReportViewTabs
                      options={REPORTS_DISTRIBUTION_VIEW_TABS}
                      value={reactivationView}
                      onChange={(value) =>
                        setReactivationView(value as ReportsDistributionView)
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                <p className="reports-recall-reactivation-control__chart-note">
                  Dormant patients returning/dormant pool
                </p>
                {reactivationEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-recall-reactivation-control__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={reactivationChartData}
                        margin={REPORT_BAR_CHART_MARGIN}
                        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                        />
                        <YAxis
                          domain={reactivationYAxis.domain}
                          ticks={reactivationYAxis.ticks}
                          tickFormatter={reactivationYAxis.tickFormatter}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={REPORT_YAXIS_WIDTH}
                        />
                        <Tooltip
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                          formatter={(value: number) =>
                            reactivationView === 'Count' ? value : `${value}%`
                          }
                        />
                        <Bar
                          dataKey="barValue"
                          fill="var(--blue-400)"
                          radius={[8, 8, 8, 8]}
                          barSize={60}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="rrc-hygiene">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column reports-patient-value-revenue__column--fill">
                <div className="reports-patient-base-health__chart-head">
                  <ReportFullViewTableTitle
                    sectionId="rrc-hygiene"
                    title="Hygiene Overdue Patients (Action List)"
                    disableEnter={hygieneOverdueEmpty}
                  />
                </div>
                <p className="reports-recall-reactivation-control__chart-note">
                  Top overdue patients with no future appointments
                </p>
                {hygieneOverdueEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-recall-reactivation-control__table-block">
                    <ReportDataTable
                      key={
                        hygieneOverdueFullView.isFullView
                          ? 'rrc-hygiene-fv'
                          : 'rrc-hygiene-card'
                      }
                      columns={hygieneOverdueColumns}
                      rows={hygieneOverdueRows}
                      rowKey={(row) => row.id}
                      initialPageSize={5}
                      className={[
                        'reports-patient-value-revenue__provider-table',
                        'reports-recall-reactivation-control__hygiene-table',
                        hygieneOverdueFullView.isFullView
                          ? 'reports-patient-value-revenue__provider-table--full-view'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      fullViewSectionId="rrc-hygiene"
                      fullViewSearchWithFilter
                    />
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>
      </div>
    </div>
  )
}

export default RecallReactivationControlReport
